import React, { useCallback, useEffect, useState } from 'react';
import { reportsAPI } from '../services/api';
import DatePicker from '../components/DatePicker';
import { useLocalization } from '../context/LocalizationContext';
import { getTodayKualaLumpurISODate, toKualaLumpurISODate } from '../utils/dateFormat';
import styles from '../styles/ReportPages.module.css';
import costStyles from '../styles/CostReport.module.css';

const fmtRM = (val) => {
  const n = Number(val) || 0;
  return `RM ${n.toLocaleString('en-MY', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const PERIOD_MODES = ['daily', 'monthly', 'yearly', 'range'];
const StatusBadge = ({ status, label }) => (
  <span className={`${styles.statusBadge} ${styles[`status_${status}`]}`}>{label || status?.replace('_', ' ')}</span>
);

const downloadCsv = (filename, rows) => {
  const escapeCell = (value) => `"${String(value ?? '').replaceAll('"', '""')}"`;
  const csv = rows.map((row) => row.map(escapeCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

const fmtCsvAmount = (val) => (
  Number(val || 0).toLocaleString('en-MY', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
);

const CostReportPage = () => {
  const { t } = useLocalization();
  const [tab, setTab] = useState('resources');
  const [reportType, setReportType] = useState('summary');
  const [mode, setMode] = useState('monthly');
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return toKualaLumpurISODate(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    return toKualaLumpurISODate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  });
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('in_progress_completed');

  const tabs = [
    { id: 'resources', label: t('cost_report_tab_resources', 'Resource Cost') },
    { id: 'users', label: t('cost_report_tab_users', 'User Cost') },
    { id: 'tasks', label: t('cost_report_tab_tasks', 'Task Cost') },
  ];

  const statusFilters = [
    { value: 'in_progress_completed', label: t('cost_report_status_in_progress_completed', 'In Progress + Completed') },
    { value: 'pending_in_progress_completed', label: t('cost_report_status_pending_in_progress_completed', 'Pending + In Progress + Completed') },
    { value: 'completed_only', label: t('cost_report_status_completed_only', 'Completed only') },
    { value: 'in_progress_only', label: t('cost_report_status_in_progress_only', 'In Progress only') },
    { value: 'pending_only', label: t('cost_report_status_pending_only', 'Pending only') },
  ];

  const modeLabelMap = {
    daily: t('common_daily', 'Daily'),
    monthly: t('common_monthly', 'Monthly'),
    yearly: t('common_yearly', 'Yearly'),
    range: t('common_range', 'Range'),
  };

  const statusLabelMap = {
    pending: t('tasks_status_pending', 'Pending'),
    in_progress: t('tasks_status_in_progress', 'In Progress'),
    completed: t('tasks_status_completed', 'Completed'),
    cancelled: t('tasks_status_cancelled', 'Cancelled'),
  };

  const handleModeChange = (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    if (nextMode === 'daily') {
      const day = getTodayKualaLumpurISODate();
      setStartDate(day);
      setEndDate(day);
    } else if (nextMode === 'monthly') {
      setStartDate(toKualaLumpurISODate(new Date(year, month, 1)));
      setEndDate(toKualaLumpurISODate(new Date(year, month + 1, 0)));
    } else if (nextMode === 'yearly') {
      setStartDate(toKualaLumpurISODate(new Date(year, 0, 1)));
      setEndDate(toKualaLumpurISODate(new Date(year, 11, 31)));
    }
  };

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { mode, startDate, endDate, statusFilter, reportType };

      let response;
      if (tab === 'resources') {
        response = await reportsAPI.costResourceReport(params);
      } else if (tab === 'users') {
        response = await reportsAPI.costUserReport(params);
      } else {
        response = await reportsAPI.costTaskReport(params);
      }

      setRows(response.data?.data || []);
      setGrandTotal(response.data?.grandTotal || 0);
    } catch (e) {
      setError(e.response?.data?.message || t('cost_report_load_failed', 'Failed to load cost report'));
      setRows([]);
      setGrandTotal(0);
    } finally {
      setLoading(false);
    }
  }, [tab, mode, startDate, endDate, statusFilter, reportType, t]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleExport = () => {
    if (tab === 'resources') {
      downloadCsv(`${t('cost_report_resource_export_filename', 'resource-cost-report')}.csv`, [
        [t('resource_report_resource', 'Resource'), t('resource_report_type', 'Type'), t('cost_report_unit_cost_rm', 'Unit Cost (RM)'), t('reports_tasks_assigned', 'Tasks Assigned'), t('cost_report_total_qty', 'Total Qty'), t('reports_total_cost', 'Total Cost (RM)')],
        ...rows.map((r) => [
          r.resource_name, r.resource_type,
          fmtCsvAmount(r.unit_cost), r.task_count,
          r.total_quantity, fmtCsvAmount(r.total_cost),
        ]),
        [t('reports_total', 'Total').toUpperCase(), '', '', '', '', fmtCsvAmount(grandTotal)],
      ]);
    } else if (tab === 'users') {
      downloadCsv(`${t('cost_report_user_export_filename', 'user-cost-report')}.csv`, [
        [t('users_username', 'Username'), t('cost_report_full_name', 'Full Name'), t('users_role', 'Role'), t('users_daily_rate_rm', 'Daily Rate (RM)'), t('reports_tasks_assigned', 'Tasks Assigned'), t('reports_total_cost', 'Total Cost (RM)')],
        ...rows.map((r) => [
          r.username, r.full_name?.trim(), r.role,
          fmtCsvAmount(r.daily_rate), r.task_count,
          fmtCsvAmount(r.total_cost),
        ]),
        [t('reports_total', 'Total').toUpperCase(), '', '', '', '', fmtCsvAmount(grandTotal)],
      ]);
    } else {
      downloadCsv(`${t('cost_report_task_export_filename', 'task-cost-report')}.csv`, [
        [t('reports_date', 'Date'), t('tasks_label_title', 'Title'), t('kanban_team', 'Team'), t('tasks_label_status', 'Status'), t('kanban_members', 'Members'), t('cost_report_resource_cost_rm', 'Resource Cost (RM)'), t('cost_report_user_cost_rm', 'User Cost (RM)'), t('task_report_total_cost_rm', 'Total Cost (RM)')],
        ...rows.map((r) => [
          r.task_date, r.title, r.team_name || '-', r.status,
          r.members || '-',
          fmtCsvAmount(r.resource_cost),
          fmtCsvAmount(r.user_cost),
          fmtCsvAmount(r.total_cost),
        ]),
        [t('reports_total', 'Total').toUpperCase(), '', '', '', '', '', '', fmtCsvAmount(grandTotal)],
      ]);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>{t('cost_report_title', 'Cost Report')}</h1>
        <p>{t('cost_report_subtitle', 'View task costs broken down by resources, users, and individual tasks. Currency: Ringgit Malaysia (RM).')}</p>
      </div>

      {/* Tabs */}
      <div className={costStyles.tabs}>
        {tabs.map((tabItem) => (
          <button
            key={tabItem.id}
            type="button"
            className={`${costStyles.tabBtn} ${tab === tabItem.id ? costStyles.tabBtnActive : ''}`}
            onClick={() => setTab(tabItem.id)}
          >
            {tabItem.label}
          </button>
        ))}
      </div>

      {/* Controls */}
      <div className={styles.controls}>
        <div className={styles.modeButtons}>
          {PERIOD_MODES.map((m) => (
            <button
              key={m}
              type="button"
              className={`${styles.modeBtn} ${mode === m ? styles.modeBtnActive : ''}`}
              onClick={() => handleModeChange(m)}
            >
              {modeLabelMap[m] || m}
            </button>
          ))}
        </div>

        {(mode === 'range' || mode === 'daily') && (
          <>
            <DatePicker label={t('tasks_filter_from', 'From')} value={startDate} onChange={setStartDate} />
            <DatePicker label={t('tasks_filter_to', 'To')} value={endDate} onChange={setEndDate} />
          </>
        )}
        {mode === 'monthly' && (
          <>
            <input
              type="month"
              className={styles.dateInput}
              value={startDate.slice(0, 7)}
              onChange={(e) => {
                const [y, m] = e.target.value.split('-').map(Number);
                setStartDate(toKualaLumpurISODate(new Date(y, m - 1, 1)));
                setEndDate(toKualaLumpurISODate(new Date(y, m, 0)));
              }}
            />
          </>
        )}
        {mode === 'yearly' && (
          <input
            type="number"
            className={styles.dateInput}
            min="2020"
            max="2100"
            value={startDate.slice(0, 4)}
            onChange={(e) => {
              const y = Number(e.target.value);
              if (y >= 2020 && y <= 2100) {
                setStartDate(`${y}-01-01`);
                setEndDate(`${y}-12-31`);
              }
            }}
          />
        )}

        <select
          className={styles.select}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          title={t('cost_report_status_filter_title', 'Task status filter for costing')}
        >
          {statusFilters.map((item) => (
            <option key={item.value} value={item.value}>{item.label}</option>
          ))}
        </select>

        <select className={styles.select} value={reportType} onChange={(e) => setReportType(e.target.value)}>
          <option value="summary">{t('reports_summary', 'Summary')}</option>
          <option value="detail">{t('reports_detail', 'Detail')}</option>
        </select>

        <button
          type="button"
          className={styles.actionBtn}
          onClick={fetchReport}
        >
          {t('tasks_refresh', 'Refresh')}
        </button>
        {rows.length > 0 && (
          <button
            type="button"
            className={styles.actionBtnSecondary}
            onClick={handleExport}
          >
            {t('cost_report_export_csv', 'Export CSV')}
          </button>
        )}
      </div>

      {/* Summary bar */}
      <div className={costStyles.summaryBar}>
        <span className={costStyles.summaryLabel}>{t('cost_report_period', 'Period')}:</span>
        <span className={costStyles.summaryValue}>{startDate} — {endDate}</span>
        <span className={costStyles.summaryDivider} />
        <span className={costStyles.summaryLabel}>{t('cost_report_cost', 'Cost')}:</span>
        <span className={costStyles.summaryTotal}>{fmtRM(grandTotal)}</span>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={costStyles.loading}>{t('cost_report_loading', 'Loading cost data...')}</div>
      ) : rows.length === 0 ? (
        <div className={costStyles.empty}>{t('cost_report_no_data', 'No cost data found for the selected period.')}</div>
      ) : (
        <>
          {/* RESOURCE COST TABLE */}
          {tab === 'resources' && reportType === 'summary' && (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={costStyles.numericHead}>#</th>
                    <th>{t('resource_report_resource', 'Resource')}</th>
                    <th>{t('resource_report_type', 'Type')}</th>
                    <th className={costStyles.currencyHead}>{t('cost_report_unit_cost', 'Unit Cost')}</th>
                    <th className={costStyles.numericHead}>{t('tasks_page_title', 'Tasks')}</th>
                    <th className={costStyles.numericHead}>{t('cost_report_total_qty_used', 'Total Qty Used')}</th>
                    <th className={costStyles.currencyHead}>{t('reports_total_cost', 'Total Cost')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={r.resource_id}>
                      <td className={costStyles.numericCell}>{idx + 1}</td>
                      <td><strong>{r.resource_name}</strong></td>
                      <td><span className={styles.teamsBadge}>{r.resource_type}</span></td>
                      <td className={costStyles.currencyCell}>{fmtRM(r.unit_cost)}</td>
                      <td className={costStyles.numericCell}>{r.task_count}</td>
                      <td className={costStyles.numericCell}>{r.total_quantity}</td>
                      <td className={`${costStyles.costCell} ${costStyles.currencyCell}`}>{fmtRM(r.total_cost)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={costStyles.totalRow}>
                    <td colSpan={6}><strong>{t('cost_report_grand_total', 'Grand Total')}</strong></td>
                    <td className={`${costStyles.costCell} ${costStyles.currencyCell}`}><strong>{fmtRM(grandTotal)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {tab === 'resources' && reportType === 'detail' && (
            <div className={styles.detailCardsWrap}>
              {rows.map((r) => (
                <div key={`resource-${r.resource_id}`} className={styles.detailCard}>
                  <div className={styles.detailCardHeader}>
                    <span className={styles.detailCardTitle}>{r.resource_name}</span>
                    <div className={styles.detailCardBadges}>
                      <span className={styles.teamsBadge}>{r.resource_type || '-'}</span>
                    </div>
                  </div>
                  <div className={styles.detailCardBody}>
                    <div className={styles.detailCardGrid}>
                      <div className={styles.detailCardField}><span className={styles.detailCardLabel}>{t('cost_report_unit_cost', 'Unit Cost')}</span><span>{fmtRM(r.unit_cost)}</span></div>
                      <div className={styles.detailCardField}><span className={styles.detailCardLabel}>{t('tasks_page_title', 'Tasks')}</span><span>{r.task_count}</span></div>
                      <div className={styles.detailCardField}><span className={styles.detailCardLabel}>{t('cost_report_total_qty_used', 'Total Qty Used')}</span><span>{r.total_quantity}</span></div>
                      <div className={styles.detailCardField}><span className={styles.detailCardLabel}>{t('reports_total_cost', 'Total Cost')}</span><span>{fmtRM(r.total_cost)}</span></div>
                    </div>

                    {Array.isArray(r.tasks) && r.tasks.length > 0 && (
                      <div className={styles.userTaskList}>
                        <div className={styles.userTaskListHeader}>{t('cost_report_tasks_using_resource', 'Tasks using this resource')}</div>
                        {r.tasks.map((task) => (
                          <div key={`${r.resource_id}-${task.task_id}`} className={styles.userTaskRow}>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('reports_date', 'Date')}</span>
                              <span className={styles.userTaskValue}>{task.task_date}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('reports_task', 'Task')}</span>
                              <span className={`${styles.userTaskValue} ${styles.userTaskTitle}`}>{task.title}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('tasks_label_team', 'Team')}</span>
                              <span className={styles.userTaskValue}>{task.team_name || '-'}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('tasks_label_status', 'Status')}</span>
                              <span className={`${styles.statusBadge} ${styles[`status_${task.status}`]}`}>{task.status?.replace('_', ' ')}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('cost_report_total_qty_used', 'Total Qty Used')}</span>
                              <span className={styles.userTaskValue}>{task.quantity_used}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('reports_total_cost', 'Total Cost')}</span>
                              <span className={styles.userTaskValue}>{fmtRM(task.task_cost)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* USER COST TABLE */}
          {tab === 'users' && reportType === 'summary' && (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={costStyles.numericHead}>#</th>
                    <th>{t('users_username', 'Username')}</th>
                    <th>{t('cost_report_full_name', 'Full Name')}</th>
                    <th>{t('users_role', 'Role')}</th>
                    <th className={costStyles.currencyHead}>{t('users_daily_rate', 'Daily Rate')}</th>
                    <th className={costStyles.numericHead}>{t('reports_tasks_assigned', 'Tasks Assigned')}</th>
                    <th className={costStyles.currencyHead}>{t('reports_total_cost', 'Total Cost')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={r.user_id}>
                      <td className={costStyles.numericCell}>{idx + 1}</td>
                      <td><strong>{r.username}</strong></td>
                      <td>{r.full_name?.trim() || '—'}</td>
                      <td>
                        <span className={`${styles.roleBadge} ${styles[`logUser_${r.role}`]}`}>{r.role}</span>
                      </td>
                      <td className={costStyles.currencyCell}>{fmtRM(r.daily_rate)}</td>
                      <td className={costStyles.numericCell}>{r.task_count}</td>
                      <td className={`${costStyles.costCell} ${costStyles.currencyCell}`}>{fmtRM(r.total_cost)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={costStyles.totalRow}>
                    <td colSpan={6}><strong>{t('cost_report_grand_total', 'Grand Total')}</strong></td>
                    <td className={`${costStyles.costCell} ${costStyles.currencyCell}`}><strong>{fmtRM(grandTotal)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {tab === 'users' && reportType === 'detail' && (
            <div className={styles.detailCardsWrap}>
              {rows.map((r) => (
                <div key={`user-${r.user_id}`} className={styles.detailCard}>
                  <div className={styles.detailCardHeader}>
                    <span className={styles.detailCardTitle}>{r.username}</span>
                    <div className={styles.detailCardBadges}>
                      <span className={`${styles.roleBadge} ${styles[`logUser_${r.role}`]}`}>{r.role}</span>
                    </div>
                  </div>
                  <div className={styles.detailCardBody}>
                    <div className={styles.detailCardGrid}>
                      <div className={styles.detailCardField}><span className={styles.detailCardLabel}>{t('cost_report_full_name', 'Full Name')}</span><span>{r.full_name?.trim() || '-'}</span></div>
                      <div className={styles.detailCardField}><span className={styles.detailCardLabel}>{t('users_daily_rate', 'Daily Rate')}</span><span>{fmtRM(r.daily_rate)}</span></div>
                      <div className={styles.detailCardField}><span className={styles.detailCardLabel}>{t('reports_tasks_assigned', 'Tasks Assigned')}</span><span>{r.task_count}</span></div>
                      <div className={styles.detailCardField}><span className={styles.detailCardLabel}>{t('reports_total_cost', 'Total Cost')}</span><span>{fmtRM(r.total_cost)}</span></div>
                    </div>

                    {Array.isArray(r.tasks) && r.tasks.length > 0 && (
                      <div className={styles.userTaskList}>
                        <div className={styles.userTaskListHeader}>{t('cost_report_tasks_assigned_user_cost', 'Tasks that make up this user cost')}</div>
                        {r.tasks.map((task) => (
                          <div key={`${r.user_id}-${task.task_id}`} className={styles.userTaskRow}>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('reports_date', 'Date')}</span>
                              <span className={styles.userTaskValue}>{task.task_date}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('reports_task', 'Task')}</span>
                              <span className={`${styles.userTaskValue} ${styles.userTaskTitle}`}>{task.title}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('tasks_label_team', 'Team')}</span>
                              <span className={styles.userTaskValue}>{task.team_name || '-'}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('tasks_label_status', 'Status')}</span>
                              <span className={`${styles.statusBadge} ${styles[`status_${task.status}`]}`}>{task.status?.replace('_', ' ')}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('reports_total_cost', 'Total Cost')}</span>
                              <span className={styles.userTaskValue}>{fmtRM(task.task_cost)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* TASK COST TABLE */}
          {tab === 'tasks' && reportType === 'summary' && (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('reports_date', 'Date')}</th>
                    <th>{t('reports_task', 'Task')}</th>
                    <th>{t('kanban_team', 'Team')}</th>
                    <th>{t('tasks_label_status', 'Status')}</th>
                    <th>{t('kanban_members', 'Members')}</th>
                    <th className={costStyles.currencyHead}>{t('cost_report_resource_cost', 'Resource Cost')}</th>
                    <th className={costStyles.currencyHead}>{t('cost_report_user_cost', 'User Cost')}</th>
                    <th className={costStyles.currencyHead}>{t('reports_total_cost', 'Total Cost')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{r.task_date}</td>
                      <td><strong>{r.title}</strong></td>
                      <td>{r.team_name || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                      <td><StatusBadge status={r.status} label={statusLabelMap[r.status]} /></td>
                      <td style={{ maxWidth: 180, fontSize: 11, color: '#475569' }}>{r.members || '—'}</td>
                      <td className={`${costStyles.costCellSub} ${costStyles.currencyCell}`}>{fmtRM(r.resource_cost)}</td>
                      <td className={`${costStyles.costCellSub} ${costStyles.currencyCell}`}>{fmtRM(r.user_cost)}</td>
                      <td className={`${costStyles.costCell} ${costStyles.currencyCell}`}>{fmtRM(r.total_cost)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={costStyles.totalRow}>
                    <td colSpan={5}><strong>{t('cost_report_grand_total', 'Grand Total')}</strong></td>
                    <td className={`${costStyles.costCellSub} ${costStyles.currencyCell}`}>—</td>
                    <td className={`${costStyles.costCellSub} ${costStyles.currencyCell}`}>—</td>
                    <td className={`${costStyles.costCell} ${costStyles.currencyCell}`}><strong>{fmtRM(grandTotal)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {tab === 'tasks' && reportType === 'detail' && (
            <div className={styles.detailCardsWrap}>
              {rows.map((r) => (
                <div key={`task-${r.id}`} className={styles.detailCard}>
                  <div className={styles.detailCardHeader}>
                    <span className={styles.detailCardTitle}>{r.title}</span>
                    <div className={styles.detailCardBadges}>
                      <StatusBadge status={r.status} label={statusLabelMap[r.status]} />
                    </div>
                  </div>
                  <div className={styles.detailCardBody}>
                    <div className={styles.detailCardGrid}>
                      <div className={styles.detailCardField}><span className={styles.detailCardLabel}>{t('reports_date', 'Date')}</span><span>{r.task_date}</span></div>
                      <div className={styles.detailCardField}><span className={styles.detailCardLabel}>{t('kanban_team', 'Team')}</span><span>{r.team_name || '-'}</span></div>
                      <div className={styles.detailCardField}><span className={styles.detailCardLabel}>{t('kanban_members', 'Members')}</span><span>{r.members || '-'}</span></div>
                      <div className={styles.detailCardField}><span className={styles.detailCardLabel}>{t('cost_report_resource_cost', 'Resource Cost')}</span><span>{fmtRM(r.resource_cost)}</span></div>
                      <div className={styles.detailCardField}><span className={styles.detailCardLabel}>{t('cost_report_user_cost', 'User Cost')}</span><span>{fmtRM(r.user_cost)}</span></div>
                      <div className={styles.detailCardField}><span className={styles.detailCardLabel}>{t('reports_total_cost', 'Total Cost')}</span><span>{fmtRM(r.total_cost)}</span></div>
                    </div>

                    {Array.isArray(r.resource_items) && r.resource_items.length > 0 && (
                      <div className={styles.userTaskList}>
                        <div className={styles.userTaskListHeader}>{t('cost_report_resource_cost_breakdown', 'Resource cost breakdown')}</div>
                        {r.resource_items.map((item, idx) => (
                          <div key={`${r.id}-resource-${idx}`} className={styles.userTaskRow}>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('resource_report_resource', 'Resource')}</span>
                              <span className={styles.userTaskValue}>{item.resource_name}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('cost_report_total_qty_used', 'Total Qty Used')}</span>
                              <span className={styles.userTaskValue}>{item.quantity}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('cost_report_unit_cost', 'Unit Cost')}</span>
                              <span className={styles.userTaskValue}>{fmtRM(item.unit_cost)}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('reports_total_cost', 'Total Cost')}</span>
                              <span className={styles.userTaskValue}>{fmtRM(item.item_cost)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {Array.isArray(r.user_items) && r.user_items.length > 0 && (
                      <div className={styles.userTaskList}>
                        <div className={styles.userTaskListHeader}>{t('cost_report_user_cost_breakdown', 'User cost breakdown')}</div>
                        {r.user_items.map((item, idx) => (
                          <div key={`${r.id}-user-${idx}`} className={styles.userTaskRow}>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('users_username', 'Username')}</span>
                              <span className={styles.userTaskValue}>{item.username}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('reports_total_cost', 'Total Cost')}</span>
                              <span className={styles.userTaskValue}>{fmtRM(item.item_cost)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CostReportPage;
